import {
  Role,
  SubscriptionRenewalBanner,
  rolesManipulator,
  rolesMapping,
  useGetAppSelectorInfo,
  useGetUserInfo,
} from 'auth';
import classNames from 'classnames';
import { useEffect } from 'react';
import {
  Link,
  Outlet,
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import { useGetDefaultBusinessId } from '@/modules/common/hooks/useGetDefaultBusinessId.ts';
import { useSidebarHeaderMenuOptions } from '@/modules/common/hooks/useSidebarHeaderMenuOptions.tsx';
import { useSidebarProfileMenuOptions } from '@/modules/common/hooks/useSidebarProfileMenuOptions.ts';
import { useSidebarOptions } from '@/modules/admin-dashboard/hooks/useSidebarOptions.tsx';
import { useContractQuery } from '@/modules/admin-dashboard/queries/contract-management.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { Sidebar, Spinner, TopBar } from 'ui';
import { LogoComplianceWhite } from 'ui/icons/logo';

const AdminComplianceDashboardWrapper = () => {
  const defaultBusinessId = useGetDefaultBusinessId();

  const { avatar } = useGetUserInfo();
  const { data: currentUser, isLoading: isUserLoading } = useCurrentUserQuery();
  const [params, setParams] = useSearchParams();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSingleFormPageNow = !!matchPath(adminDashboardPaths.dynamic.form(), pathname);
  const contractRouteMatch = matchPath(adminDashboardPaths.dynamic.contract(), pathname);
  const contractId = Number(contractRouteMatch?.params.id);
  const { data: contractData } = useContractQuery(contractId, !contractId);
  const shouldCollapseSidebarByDefault =
    !!contractRouteMatch && (contractData?.documents?.length || 0) > 1;

  const menuOptions = useSidebarOptions();
  const profileOptions = useSidebarProfileMenuOptions();
  const appSelectorInfo = useGetAppSelectorInfo();
  const { items: headerMenuItems, isLoading: isHeaderMenuItemsLoading } =
    useSidebarHeaderMenuOptions();

  const selectedBusinessId = params.get(BUSINESS_QUERY_PARAM) || undefined;
  const selectedBusiness = (currentUser?.businesses || []).find(
    ({ id }) => id === +(selectedBusinessId || '0'),
  );
  const selectedBusinessMenuItem = headerMenuItems.find(
    ({ value }) => value === selectedBusinessId,
  );

  useEffect(() => {
    //If business id is incorrect
    if (!!selectedBusinessId && !selectedBusiness && currentUser?.businesses) {
      params.set(BUSINESS_QUERY_PARAM, String(currentUser?.businesses?.[0]?.id));
      setParams(params);
    }
  }, [currentUser?.businesses, params, selectedBusiness, selectedBusinessId, setParams]);

  useEffect(() => {
    if (!selectedBusinessId && defaultBusinessId) {
      params.set(BUSINESS_QUERY_PARAM, defaultBusinessId);
      setParams(params);

      if (pathname.includes('/forms-by-tag/')) {
        navigate({
          pathname: adminDashboardPaths.static.forms,
          search: params.toString(),
        });
      }
    }
  }, [defaultBusinessId, navigate, params, pathname, selectedBusinessId, setParams]);

  return (
    <div
      className={classNames(
        'bg-gray-900 sm:h-screen w-full flex sm:flex-row flex-col h-[calc(100%-56px)]',
      )}
    >
      {!isSingleFormPageNow && (
        <Sidebar
          searchField
          defaultOpened={!shouldCollapseSidebarByDefault}
          label={
            <Link to={{ pathname: adminDashboardPaths.static.dashboard, search: location.search }}>
              <img alt="logo" src={LogoComplianceWhite} className="h-8" />
            </Link>
          }
          disabledHeaderMenu={headerMenuItems.length < 2}
          avatarUrl={avatar}
          menuItems={menuOptions}
          profileMenuItems={profileOptions}
          headerMenuItems={headerMenuItems}
          headerMenuSelectedItem={selectedBusinessMenuItem}
          isHeaderMenuItemsLoading={isHeaderMenuItemsLoading}
          user={{ ...currentUser?.user, business: selectedBusiness }}
          compliance={true}
          appSelectorInfo={appSelectorInfo}
          role={
            rolesManipulator?.current_user_role
              ? rolesMapping[rolesManipulator?.current_user_role].name
              : ''
          }
        />
      )}
      <TopBar
        label={
          <Link to={{ pathname: adminDashboardPaths.static.dashboard, search: location.search }}>
            <img alt="logo" src={LogoComplianceWhite} className="h-8" />
          </Link>
        }
        avatarUrl={avatar}
        header="menu"
        menuItems={menuOptions}
        profileMenuItems={profileOptions}
        headerMenuItems={headerMenuItems}
        headerMenuSelectedValue={selectedBusinessId}
        user={{ ...currentUser?.user, business: selectedBusiness }}
        compliance={true}
        appSelectorInfo={appSelectorInfo}
      />

      {isUserLoading || !selectedBusiness ? (
        <main className="bg-white flex w-full h-full justify-center items-center">
          <Spinner size="lg" />
        </main>
      ) : (
        <main
          className={classNames(
            'overflow-auto flex flex-col w-full sm:flex-grow-0 flex-grow relative top-0 bottom-0',
            'bg-white items-start justify-center',
            {
              'rounded-tl-xl sm:h-full h-[calc(100%-56px)]': !isSingleFormPageNow,
              'h-screen': isSingleFormPageNow,
              'rounded-tr-xl sm:rounded-tr-none sm:rounded-bl-xl rounded-bl-none':
                !isSingleFormPageNow,
            },
          )}
        >
          <SubscriptionRenewalBanner
            identityCenterApiUrl={import.meta.env.VITE_IDENTITY_CENTER_API_URL}
            selectedApp="compliance"
            storageScope="compliance-admin"
          />
          <Outlet />
        </main>
      )}
    </div>
  );
};

export default withPrivateAccess(AdminComplianceDashboardWrapper, Role.COMPLIANCE_ADMIN);
